import React from 'react';
import { styled } from '@linaria/react';

import Slider, { Props as SliderProps } from '@/components/Slider';

import ControlWrapper from './ControlWrapper';
import ValuePreview from './ValuePreview';
import Notch from './SliderNotch';

export interface Props extends SliderProps {
  id: string;
  label: React.ReactNode;
  secondaryLabel?: string;
  value: number;
  setValue: (value: number) => void;
  definition?: string;
  previewRenderer?: (value: string | number) => React.ReactNode;
  gridArea?: string;
  wrapperStyle?: React.CSSProperties;
  disabled?: boolean;
  // Some sliders, like the one in ArcRotateDemo, have a notch to indicate an important / key value.
  notchValue?: number;
  // NOTE: we use `setValue` for changing the value of the slider.
  // `onChange` is used for side-effects, like playing a sound effect when the value changes.
  onChange?: (value: number) => void;
  handleReset?: () => void;
}

function SliderControl({
  id,
  label,
  secondaryLabel,
  value,
  setValue,
  definition,
  previewRenderer,
  gridArea,
  wrapperStyle,
  disabled,
  notchValue,
  onChange,
  handleReset,
  ...delegated
}: Props) {
  return (
    <ControlWrapper
      id={id}
      label={label}
      secondaryLabel={secondaryLabel}
      definition={definition}
      gridArea={gridArea}
      style={wrapperStyle}
      disabled={disabled}
      cornerContents={
        <ValuePreview value={value} renderer={previewRenderer} />
      }
    >
      {typeof notchValue === 'number' && (
        <Notch
          min={delegated.min || 0}
          max={delegated.max || 100}
          value={notchValue}
          currentValue={value}
        />
      )}
      <Slider
        id={id}
        value={value}
        disabled={disabled}
        onKeyDown={(ev) => {
          if (ev.key === 'Delete' || ev.key === 'Backspace') {
            handleReset?.();
          }
        }}
        {...delegated}
        onChange={(val) => {
          setValue(val);

          if (typeof onChange === 'function') {
            onChange(val);
          }
        }}
      />
    </ControlWrapper>
  );
}

export default SliderControl;
